import { PortalDialogProps } from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import { Form, Formik, FormikContextType } from "formik";
import { FormControl } from "../form-control";
import Button from "../button";
import Input from "../input";
import { createRef, useEffect, useState } from "react";
import useObservable from "@core/hooks/use-observable.hook";
import { CustomSelect } from "../select/custom-select";
import TriangleDownArrow from "../select/triangle-down-arrow";
import { NormalSelectOptions } from "../select/select.type";
import { updateDeviceValidationSchema } from "@app/validations";
import { Account, UpdateDeviceRequest } from "@app/types";
import { DeviceStatus, DeviceType } from "@app/constants";
import DeviceService from "@app/services/http/device.service";
import AccountService from "@app/services/http/account.service";

function UpdateDeviceModal({ portalDialogRef, portalData }: PortalDialogProps) {
  const formRef = createRef<FormikContextType<UpdateDeviceRequest>>();
  const isCompany = portalData.isCompany;

  const [accounts, setAccounts] = useState<Account[]>([]);

  const { subscribeOnce } = useObservable();

  useEffect(() => {
    !isCompany &&
      subscribeOnce(AccountService.getAccounts<Account[]>(1, "true"), (res) => {
        setAccounts(res);
      });
  }, []);

  const selectDeviceStatus: NormalSelectOptions = [
    {
      label: DeviceStatus.ACTIVE,
      value: DeviceStatus.ACTIVE,
    },
    {
      label: DeviceStatus.IN_ACTIVE,
      value: DeviceStatus.IN_ACTIVE,
    },
  ];

  const selectDeviceType: NormalSelectOptions = [
    {
      label: DeviceType.DOWNLOAD,
      value: DeviceType.DOWNLOAD,
    },
    {
      label: DeviceType.CALL,
      value: DeviceType.CALL,
    },
    {
      label: DeviceType.ANSWER,
      value: DeviceType.ANSWER,
    },
  ];

  const { subscribeUntilDestroy } = useObservable();

  const initialValues = {
    building_name: portalData.device?.building_name || "",
    device_name: portalData.device?.device_name || "",
    network_provider: portalData.device?.network_provider || "",
    phone: portalData.device?.phone || "",
    type: portalData.device?.type || DeviceType.DOWNLOAD,
    status: portalData.device?.status || DeviceStatus.ACTIVE,
    level: portalData.device?.level || "",
    min_time_call: portalData.device.min_time_call || "",
    max_time_call: portalData.device.max_time_call || "",
    company: portalData.device?.company?._id || "",
  };

  const handleSubmit = (values: UpdateDeviceRequest) => {
    subscribeUntilDestroy(
      DeviceService.updateDevice(portalData.device._id as string, values),
      (device) => {
        portalDialogRef.close({ device });
      }
    );
  };

  return (
    <Modal onCancel={portalDialogRef.close} buttonCancelInChildren>
      <div className="w-[300px] sm:w-[500px] p-5">
        <div className="font-bold text-xl">Cập nhật thông tin thiết Bị</div>
        <Formik
          initialValues={initialValues}
          validationSchema={updateDeviceValidationSchema}
          innerRef={formRef}
          onSubmit={handleSubmit}
          validateOnChange
          validateOnBlur
        >
          <Form>
            <div className="flex justify-between gap-5">
              <div className="flex-1">
                <div className="flex gap-2 items-center justify-between">
                  <div className="mt-3 flex-1">
                    <div className="text-sm">Tên tòa nhà</div>
                    <FormControl name="building_name">
                      <Input
                        className="mt-1 !max-w-none text-sm"
                        errorClassName="text-sm"
                      />
                    </FormControl>
                  </div>
                  <div className="mt-3 flex-1">
                    <div className="text-sm">Tên thiết bị</div>
                    <FormControl name="device_name">
                      <Input
                        className="mt-1 !max-w-none text-sm"
                        errorClassName="text-sm"
                      />
                    </FormControl>
                  </div>
                </div>
                {!isCompany && (
                  <div>
                    <div className="flex gap-2 items-center justify-between">
                      <div className="mt-3 flex-1">
                        <div className="text-sm">Nhà Mạng</div>
                        <FormControl name="network_provider">
                          <Input
                            className="mt-1 !max-w-none text-sm"
                            errorClassName="text-sm"
                          />
                        </FormControl>
                      </div>
                      <div className="mt-3 flex-1">
                        <div className="text-sm">Số điện thoại</div>
                        <FormControl name="phone">
                          <Input
                            className="mt-1 !max-w-none text-sm"
                            errorClassName="text-sm"
                          />
                        </FormControl>
                      </div>
                    </div>
                    <div className="flex gap-2 items-center justify-between">
                      <div className="mt-3 flex-1">
                        <div className="text-sm">Loại máy</div>
                        <FormControl name="type">
                          <CustomSelect
                            options={selectDeviceType}
                            width="100%"
                            className="!h-9 !border-[#3A466480] !rounded-lg"
                            optionContainerClassName="top-[34px] w-full"
                            optionClassName="!min-h-[35px] text-sm text-black whitespace-nowrap px-2"
                            labelSelectedClassName="text-black text-sm"
                            errorClassName="!text-[#D60000B2] text-sm"
                            arrow={<TriangleDownArrow />}
                            hasBgColorHover
                          />
                        </FormControl>
                      </div>
                      <div className="mt-3 flex-1">
                        <div className="text-sm">Trạng thái</div>
                        <FormControl name="status">
                          <CustomSelect
                            options={selectDeviceStatus}
                            width="100%"
                            className="!h-9 !border-[#3A466480] !rounded-lg"
                            optionContainerClassName="top-[34px] w-full"
                            optionClassName="!min-h-[35px] text-sm text-black whitespace-nowrap px-2"
                            labelSelectedClassName="text-black text-sm"
                            errorClassName="!text-[#D60000B2] text-sm"
                            arrow={<TriangleDownArrow />}
                            hasBgColorHover
                          />
                        </FormControl>
                      </div>
                    </div>
                    {portalData.device?.type === DeviceType.DOWNLOAD ? (
                      <div>
                        <div className="mt-3 flex-1">
                          <div className="text-sm">Level Link</div>
                          <FormControl name="level">
                            <Input
                              type="number"
                              className="mt-1 !max-w-none text-sm"
                              errorClassName="text-sm"
                            />
                          </FormControl>
                        </div>
                      </div>
                    ) : (
                      portalData.device.type === DeviceType.CALL && (
                        <div className="flex gap-2 items-center justify-between">
                          <div className="mt-3 flex-1">
                            <div className="text-sm">Gọi tối thiểu(phút)</div>
                            <FormControl name="min_time_call">
                              <Input
                                type="number"
                                className="mt-1 !max-w-none text-sm"
                                errorClassName="text-sm"
                              />
                            </FormControl>
                          </div>
                          <div className="mt-3 flex-1">
                            <div className="text-sm">Gọi tối đa</div>
                            <FormControl name="max_time_call">
                              <Input
                                type="number"
                                className="mt-1 !max-w-none text-sm"
                                errorClassName="text-sm"
                              />
                            </FormControl>
                          </div>
                        </div>
                      )
                    )}
                    <div className="mt-3 flex-1">
                      <div className="text-sm">Công ty</div>
                      <FormControl name="company">
                        <CustomSelect
                          options={accounts.map((acc) => ({
                            label: acc.name,
                            value: acc._id,
                          }))}
                          width="100%"
                          className="!h-9 !border-[#3A466480] !rounded-lg"
                          optionContainerClassName="top-[34px] w-full"
                          optionClassName="!min-h-[35px] text-sm text-black whitespace-nowrap px-2"
                          labelSelectedClassName="text-black text-sm"
                          errorClassName="!text-[#D60000B2] text-sm"
                          arrow={<TriangleDownArrow />}
                          hasBgColorHover
                        />
                      </FormControl>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <Button
              type="submit"
              label="Cập nhật"
              containerClassName="mt-5 mx-auto"
              width="fit-content"
              className="px-6 text-sm"
              labelClassName="font-bold"
            />
          </Form>
        </Formik>
      </div>
    </Modal>
  );
}

export default UpdateDeviceModal;
